import React, { useEffect, useMemo } from "react";
import { Stack } from "@mui/material";
import { Formik, Form, FormikProps } from "formik";
import * as Yup from "yup";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";

import FormAlert from "../components/common/FormAlert";
import FormSelectField from "../components/common/FormSelectField";
import SubmitButton from "../components/buttons/SubmitButton";

import { useAppDispatch, useAppSelector } from "../redux/store";
import {
  createTeacherSubject,
  updateTeacherSubject,
} from "../redux/actions/teacherSubjectActions";
import { getTeachers } from "../redux/actions/teacherActions";
import { getSubjects } from "../redux/actions/subjectActions";
import { getAcademicYears } from "../redux/actions/academicYearActions";
import { teacherSubjectActions } from "../redux/slices/teacherSubject";
import { cleanFormData } from "../helpers/formHelpers";

import { TeacherSubjectResponseDto } from "../shared/dtos/teacherSubject/TeacherSubjectResponseDto";
import { TeacherSubjectRequestDto } from "../shared/dtos/teacherSubject/TeacherSubjectRequestDto";

const teacherSubjectSchema = Yup.object().shape({
  teacherId: Yup.string().required("Teacher selection is required"),
  subjectId: Yup.string().required("Subject selection is required"),
  academicYearId: Yup.string().required("Academic Year is required"),
});

interface CreateOrUpdateTeacherSubjectFormProps {
  teacherSubject?: TeacherSubjectResponseDto | null;
  isEditing?: boolean;
  loading?: boolean;
  error?: string | null;
}

const CreateOrUpdateTeacherSubjectForm: React.FC<
  CreateOrUpdateTeacherSubjectFormProps
> = ({
  teacherSubject = null,
  isEditing = false,
  loading = false,
  error = null,
}) => {
  const dispatch = useAppDispatch();

  // Redux Selectors
  const { items: teachers } = useAppSelector((state) => state.teacher);
  const { items: subjects } = useAppSelector((state) => state.subject);
  const { items: academicYears } = useAppSelector(
    (state) => state.academicYear,
  );

  useEffect(() => {
    if (!teachers || teachers.length === 0) dispatch(getTeachers());
    if (!subjects || subjects.length === 0) dispatch(getSubjects());
    if (!academicYears || academicYears.length === 0)
      dispatch(getAcademicYears());
  }, [dispatch, teachers, subjects, academicYears]);

  const teacherOptions = useMemo(
    () =>
      teachers?.map((t) => ({
        value: t.id,
        label: t.name || "Unknown Teacher",
      })) || [],
    [teachers],
  );

  const subjectOptions = useMemo(
    () => subjects?.map((s) => ({ value: s.id, label: s.name })) || [],
    [subjects],
  );

  const yearOptions = useMemo(
    () => academicYears?.map((y) => ({ value: y.id, label: y.name })) || [],
    [academicYears],
  );

  const initialValues: TeacherSubjectRequestDto = {
    teacherId: teacherSubject?.teacherId || "",
    subjectId: teacherSubject?.subjectId || "",
    academicYearId: teacherSubject?.academicYearId || "",
  };

  return (
    <Stack spacing={2} sx={{ width: "100%", textAlign: "center" }}>
      <FormAlert
        message={error}
        severity="error"
        closable
        onClose={
          isEditing
            ? () => dispatch(teacherSubjectActions.setErrorUpdate(null))
            : () => dispatch(teacherSubjectActions.setErrorCreate(null))
        }
      />

      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={teacherSubjectSchema}
        onSubmit={(values) => {
          const rawCleaned = cleanFormData(values) as Record<string, any>;
          const cleanedValues: TeacherSubjectRequestDto = {
            teacherId: rawCleaned.teacherId,
            subjectId: rawCleaned.subjectId,
            academicYearId: rawCleaned.academicYearId,
          };

          if (isEditing && teacherSubject) {
            dispatch(
              updateTeacherSubject({
                id: teacherSubject.id,
                values: cleanedValues,
              }),
            );
          } else {
            dispatch(createTeacherSubject(cleanedValues));
          }
        }}
      >
        {({ dirty, isValid }: FormikProps<TeacherSubjectRequestDto>) => (
          <Form>
            <Stack spacing={2}>
              <FormSelectField
                name="teacherId"
                label="Assign Teacher"
                options={teacherOptions}
              />

              <FormSelectField
                name="subjectId"
                label="Subject"
                options={subjectOptions}
              />

              <FormSelectField
                name="academicYearId"
                label="Academic Year"
                options={yearOptions}
              />

              <SubmitButton
                title={isEditing ? "Save Changes" : "Assign Subject"}
                loadingText={isEditing ? "Saving..." : "Assigning..."}
                loading={loading}
                disabled={!dirty || !isValid || loading}
                startIcon={
                  isEditing ? <SaveOutlinedIcon /> : <AssignmentIndIcon />
                }
                sx={{ mt: 1 }}
              />
            </Stack>
          </Form>
        )}
      </Formik>
    </Stack>
  );
};

export default CreateOrUpdateTeacherSubjectForm;
